// Debate-starts step animation geometry + timing (from debate-starts prototype).

/** Figma Web app → node 4427:65 halftone band behind the animation. */
export const DEBATE_STARTS_BACKGROUND_SRC =
  '/images/support-mainnet/how-it-works-debate-starts-background.png' as const;

export const DEBATE_STARTS_ARTBOARD = {
  width: 540,
  height: 360,
} as const;

export const DEBATE_STARTS_SURFACE = {
  bubble: { width: 344, height: 80, left: 98, top: 140 },
  card: { width: 480, height: 280, left: 30, top: 46 },
  collapsed: { width: 480, height: 101, left: 30, top: 46 },
} as const;

export const DEBATE_STARTS_METER = {
  cols: 78,
  rows: 3,
  width: 440,
  height: 14,
  dotRadius: 1.7,
  fill: '#82d675',
  empty: '#313131',
} as const;

export const DEBATE_STARTS_TIMING = {
  morphToCardMs: 1700,
  meterStartMs: 3500,
  meterDurationMs: 2400,
  chipBaseMs: 3700,
  chipStaggerMs: 460,
  collapseMs: 6500,
  outcomeBaseMs: 6900,
  outcomeStaggerMs: 160,
  intersectionThreshold: 0.4,
  meterTargetFrac: 0.99,
  finalAmountEur: 990,
  finalPct: 99,
  finalUsers: 1242,
} as const;

export const DEBATE_STARTS_OUTCOMES = [
  {
    id: 'o1',
    label: 'Yes, they stole it',
    dotClass: 'debate-starts-odot-orange',
    style: { left: 34, top: 164, rotate: -5 },
  },
  {
    id: 'o2',
    label: 'No, Trump lost fairly',
    dotClass: 'debate-starts-odot-lime',
    style: { left: 150, top: 224, rotate: 4 },
  },
  {
    id: 'o3',
    label: 'Both sides cheat',
    dotClass: 'debate-starts-odot-cyan',
    style: { right: 36, top: 174, rotate: 6 },
  },
] as const;

export const DEBATE_STARTS_CHIPS = [
  {
    name: 'Mark112 backed',
    amount: '$125.50',
    avatarClass: 'debate-starts-chip-av-1',
    style: { left: 300, top: 8 },
  },
  {
    name: 'jules.eth backed',
    amount: '$80.00',
    avatarClass: 'debate-starts-chip-av-2',
    style: { right: -6, top: 116 },
  },
  {
    name: '0xKade backed',
    amount: '$210.00',
    avatarClass: 'debate-starts-chip-av-3',
    style: { left: -8, top: 226 },
  },
  {
    name: 'Mara backed',
    amount: '$300.00',
    avatarClass: 'debate-starts-chip-av-4',
    style: { right: 24, bottom: 16 },
  },
] as const;

export type DebateStartsMeterDot = {
  col: number;
  row: number;
  cx: number;
  cy: number;
};

export function easeOutCubic(progress: number): number {
  return 1 - (1 - progress) ** 3;
}

export function computeArtboardScale(containerWidth: number, containerHeight: number): number {
  if (containerWidth <= 0 || containerHeight <= 0) {
    return 1;
  }

  return Math.min(
    1,
    containerWidth / DEBATE_STARTS_ARTBOARD.width,
    containerHeight / DEBATE_STARTS_ARTBOARD.height,
  );
}

export function buildMeterDots(): DebateStartsMeterDot[] {
  const { cols, rows, width, height } = DEBATE_STARTS_METER;
  const stepX = width / cols;
  const stepY = height / rows;
  const dots: DebateStartsMeterDot[] = [];

  for (let col = 0; col < cols; col += 1) {
    for (let row = 0; row < rows; row += 1) {
      dots.push({
        col,
        row,
        cx: Number((col * stepX + stepX / 2).toFixed(1)),
        cy: Number((row * stepY + stepY / 2).toFixed(1)),
      });
    }
  }

  return dots;
}

export function meterFilledColumns(fraction: number): number {
  return Math.round(fraction * DEBATE_STARTS_METER.cols);
}

export function meterValuesForFraction(fraction: number): {
  meterFrac: number;
  amountEur: number;
  pct: number;
  users: number;
} {
  const { meterTargetFrac, finalAmountEur, finalPct, finalUsers } = DEBATE_STARTS_TIMING;
  const progress = fraction * meterTargetFrac;
  return {
    meterFrac: progress,
    amountEur: Math.round(fraction * finalAmountEur),
    pct: Math.round(fraction * finalPct),
    users: Math.round(fraction * finalUsers),
  };
}

/** Longest scripted timer plus outcome pop transition (debate-starts-animation.css). */
export function getDebateStartsPlaybackDurationMs(): number {
  const { outcomeBaseMs, outcomeStaggerMs } = DEBATE_STARTS_TIMING;
  const lastOutcomeMs = outcomeBaseMs + (DEBATE_STARTS_OUTCOMES.length - 1) * outcomeStaggerMs;
  return lastOutcomeMs + 620;
}
